import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { plantillaService } from '../services/presupuestoProformaService';
import type { Plantilla, PlantillaFormData, PlantillaItemFormData } from '../types';
import { aplicarOrden } from '../utils/orden';

export const PLANTILLAS_QUERY_KEY = ['presupuestos', 'plantillas'] as const;

const listadoKey = (soloActivas: boolean) => [...PLANTILLAS_QUERY_KEY, 'list', soloActivas] as const;
const detalleKey = (id: string) => [...PLANTILLAS_QUERY_KEY, 'detalle', id] as const;

/**
 * Listado de plantillas de ítems de presupuesto. Con `soloActivas`, las que se
 * ofrecen para crear o importar en un presupuesto.
 */
export const usePlantillas = ({ soloActivas = false, enabled = true } = {}) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: listadoKey(soloActivas),
    queryFn: () => plantillaService.listar(soloActivas),
    enabled,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: PLANTILLAS_QUERY_KEY });

  const crearMutation = useMutation({
    mutationFn: (payload: PlantillaFormData & { nombre: string }) => plantillaService.crear(payload),
    onSuccess: invalidar,
  });

  const actualizarMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: PlantillaFormData }) =>
      plantillaService.actualizar(id, data),
    onSuccess: invalidar,
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => plantillaService.eliminar(id),
    onSuccess: invalidar,
  });

  const reordenarMutation = useMutation({
    mutationFn: (ids: string[]) => plantillaService.reordenar(ids),
    // Si falló, también se recarga: vuelve el orden guardado.
    onSettled: invalidar,
  });

  /**
   * Guarda el orden arrastrado. Se aplica en la caché antes de la request para
   * que la lista no vuelva un instante al orden anterior.
   */
  const reordenarPlantillas = (ids: string[]) => {
    queryClient.setQueryData<Plantilla[] | undefined>(listadoKey(soloActivas), (actual) =>
      actual ? aplicarOrden(actual, ids) : actual
    );

    return reordenarMutation.mutateAsync(ids);
  };

  const duplicarMutation = useMutation({
    mutationFn: (id: string) => plantillaService.duplicar(id),
    onSuccess: invalidar,
  });

  return {
    plantillas: data ?? ([] as Plantilla[]),
    isLoading,
    error,
    crearPlantilla: crearMutation.mutateAsync,
    actualizarPlantilla: actualizarMutation.mutateAsync,
    eliminarPlantilla: eliminarMutation.mutateAsync,
    reordenarPlantillas,
    duplicarPlantilla: duplicarMutation.mutateAsync,
    isGuardando:
      crearMutation.isPending ||
      actualizarMutation.isPending ||
      eliminarMutation.isPending ||
      reordenarMutation.isPending ||
      duplicarMutation.isPending,
  };
};

/** Detalle de una plantilla con la autogestión de sus ítems. */
export const usePlantilla = (id: string) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: detalleKey(id),
    queryFn: () => plantillaService.obtener(id),
    enabled: !!id,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: PLANTILLAS_QUERY_KEY });

  const actualizarMutation = useMutation({
    mutationFn: (payload: PlantillaFormData) => plantillaService.actualizar(id, payload),
    onSuccess: invalidar,
  });

  const crearItemMutation = useMutation({
    mutationFn: (payload: PlantillaItemFormData) => plantillaService.crearItem(id, payload),
    onSuccess: invalidar,
  });

  const actualizarItemMutation = useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: PlantillaItemFormData }) =>
      plantillaService.actualizarItem(id, itemId, data),
    onSuccess: invalidar,
  });

  const eliminarItemMutation = useMutation({
    mutationFn: (itemId: string) => plantillaService.eliminarItem(id, itemId),
    onSuccess: invalidar,
  });

  const reordenarItemsMutation = useMutation({
    mutationFn: (itemIds: string[]) => plantillaService.reordenarItems(id, itemIds),
    // Si falló, también se recarga: vuelve el orden guardado.
    onSettled: invalidar,
  });

  /**
   * Se aplica en la caché antes de la request para que la lista no vuelva un
   * instante al orden anterior. Los ids son de una sola parte: las demás no cambian.
   */
  const reordenarItems = (itemIds: string[]) => {
    queryClient.setQueryData<Plantilla | undefined>(detalleKey(id), (actual) =>
      actual
        ? {
          ...actual,
          items: Object.fromEntries(
            Object.entries(actual.items).map(([parte, items]) => [parte, aplicarOrden(items, itemIds)])
          ) as Plantilla['items'],
        }
        : actual
    );

    return reordenarItemsMutation.mutateAsync(itemIds);
  };

  const duplicarMutation = useMutation({
    mutationFn: () => plantillaService.duplicar(id),
    onSuccess: invalidar,
  });

  return {
    plantilla: data,
    isLoading,
    error,
    actualizarPlantilla: actualizarMutation.mutateAsync,
    crearItem: crearItemMutation.mutateAsync,
    actualizarItem: actualizarItemMutation.mutateAsync,
    eliminarItem: eliminarItemMutation.mutateAsync,
    reordenarItems,
    duplicarPlantilla: duplicarMutation.mutateAsync,
    isGuardando:
      duplicarMutation.isPending ||
      actualizarMutation.isPending ||
      crearItemMutation.isPending ||
      actualizarItemMutation.isPending ||
      eliminarItemMutation.isPending ||
      reordenarItemsMutation.isPending,
  };
};
